// frontend/src/components/auditor/AuditorCasesClients.js
// Cases and clients read-only tables for audit review.

import React, { useState, useEffect, useCallback } from 'react';
import AuditorTable from './AuditorTable';
import { fetchAllPages } from '../../utils/auditorApi';

const SUB_TABS = [
  { id: 'cases', label: 'Cases' },
  { id: 'clients', label: 'Clients' },
];

const fmtDate = (v) => v ? new Date(v).toLocaleDateString() : '—';

const StageBadge = ({ stage }) => {
  const colors = {
    'Open': 'bg-green-100 text-green-700',
    'In Progress': 'bg-blue-100 text-blue-700',
    'Closed': 'bg-gray-200 text-gray-600',
    'Archived': 'bg-slate-200 text-slate-600',
    'On Hold': 'bg-amber-100 text-amber-700',
    'Pending': 'bg-yellow-100 text-yellow-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[stage] || 'bg-gray-100 text-gray-600'}`}>
      {stage || '—'}
    </span>
  );
};

export default function AuditorCasesClients({ authAxios }) {
  const [subTab, setSubTab] = useState('cases');
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    const [c, cl] = await Promise.all([
      fetchAllPages(authAxios, 'cases/'),
      fetchAllPages(authAxios, 'clients/'),
    ]);
    setCases(c);
    setClients(cl);
    if (c.length === 0 && cl.length === 0) {
      setFetchError('No case/client data was returned. Check the browser console for API errors.');
    }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  // Stats
  const openCases = cases.filter(c => c.status === 'Open' || c.stage === 'Open' || (!c.is_closed && !c.is_archived)).length;
  const closedCases = cases.filter(c => c.status === 'Closed' || c.stage === 'Closed' || c.is_closed).length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Cases &amp; Clients</h2>
        <p className="text-sm text-gray-500 mt-1">All firm cases and client records.</p>
      </div>

      {fetchError && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 text-sm flex items-start gap-2">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <span>{fetchError}</span>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Cases', value: cases.length, color: 'text-blue-700 bg-blue-50 border-blue-200' },
          { label: 'Open / Active', value: openCases, color: 'text-green-700 bg-green-50 border-green-200' },
          { label: 'Closed', value: closedCases, color: 'text-gray-700 bg-gray-50 border-gray-200' },
          { label: 'Total Clients', value: clients.length, color: 'text-indigo-700 bg-indigo-50 border-indigo-200' },
        ].map((c, i) => (
          <div key={i} className={`rounded-xl border p-4 ${c.color}`}>
            <p className="text-xs uppercase tracking-wider opacity-70 mb-1">{c.label}</p>
            <p className="text-2xl font-bold">{c.value}</p>
          </div>
        ))}
      </div>

      {/* Sub-tabs */}
      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 w-fit">
        {SUB_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              subTab === t.id ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'cases' && (
        <AuditorTable
          title={`Cases (${cases.length})`}
          data={cases}
          columns={[
            { key: 'reference', label: 'Reference' },
            { key: 'title', label: 'Title' },
            { key: 'client_name', label: 'Client', render: (v, row) => row.client_name || row.client_details?.name || '—' },
            { key: 'case_type', label: 'Type', render: (v, row) => row.case_type || row.case_type_details?.name || '—' },
            { key: 'stage', label: 'Stage', render: (v) => <StageBadge stage={v} /> },
            { key: 'responsible_attorney_name', label: 'Attorney', render: (v, row) =>
              row.responsible_attorney_name || row.responsible_attorney_details?.full_name || '—'
            },
            { key: 'date_opened', label: 'Opened', render: (v) => fmtDate(v) },
            { key: 'date_closed', label: 'Closed', render: (v) => fmtDate(v) },
          ]}
          exportCsv={{ filename: 'cases.csv' }}
          emptyMessage="No cases found."
        />
      )}

      {subTab === 'clients' && (
        <AuditorTable
          title={`Clients (${clients.length})`}
          data={clients}
          columns={[
            { key: 'name', label: 'Name', render: (v, row) => row.name || `${row.first_name || ''} ${row.last_name || ''}`.trim() || '—' },
            { key: 'client_type', label: 'Type', render: (v) => (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                v === 'Individual' || v === 'INDIVIDUAL' ? 'bg-blue-100 text-blue-700' :
                v === 'Organization' || v === 'ORGANIZATION' ? 'bg-purple-100 text-purple-700' :
                'bg-gray-100 text-gray-600'
              }`}>{v || '—'}</span>
            )},
            { key: 'email', label: 'Email' },
            { key: 'phone', label: 'Phone', render: (v, row) => row.phone || row.phone_number || '—' },
            { key: 'id_number', label: 'ID / Reg #', render: (v, row) => row.id_number || row.registration_number || '—' },
            { key: 'created_at', label: 'Since', render: (v) => fmtDate(v) },
            { key: 'is_active', label: 'Active', render: (v) => (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                v !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>{v !== false ? 'Yes' : 'No'}</span>
            )},
          ]}
          exportCsv={{ filename: 'clients.csv' }}
          emptyMessage="No clients found."
        />
      )}
    </div>
  );
}
